import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import useUIStore from '@/app/store/ui.store';
import { useAuth } from '@/shared/hooks/useAuth';
import { useInboxCount } from '@/shared/hooks/useInboxCount';
import { Icon, Avatar } from '@/design-system/primitives';
import { PersonaSwitcher } from './PersonaSwitcher';

const ACCENT_HUES: { hue: number; label: string; sample: string }[] = [
  { hue: 220, label: 'Bleu', sample: 'oklch(0.55 0.17 220)' },
  { hue: 160, label: 'Vert', sample: 'oklch(0.55 0.17 160)' },
  { hue: 280, label: 'Violet', sample: 'oklch(0.55 0.17 280)' },
  { hue: 20, label: 'Orange', sample: 'oklch(0.55 0.17 20)' },
];

const DENSITIES: { value: 'compact' | 'comfortable' | 'roomy'; label: string }[] = [
  { value: 'compact', label: 'Compact' },
  { value: 'comfortable', label: 'Confort' },
  { value: 'roomy', label: 'Aéré' },
];

export const AgencyHeader: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toggleSidebarCollapsed, theme, setTheme, density, setDensity, accentHue, setAccentHue } = useUIStore();
  const { data: inbox } = useInboxCount();
  const isDark = theme === 'dark';

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const onSearchOpen = () => toast.info('Recherche globale bientôt disponible');
  const onBellClick = () => toast.info('Aucune nouvelle notification');
  const onLogout = () => {
    setMenuOpen(false);
    navigate('/login');
  };
  const toggleTheme = () => setTheme(isDark ? 'light' : 'dark');

  const displayName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.username || 'Utilisateur';

  return (
    <header className="h-[56px] flex items-center gap-3 px-4 border-b border-border bg-surface/80 backdrop-blur sticky top-0 z-40">
      <button
        type="button"
        onClick={toggleSidebarCollapsed}
        className="w-9 h-9 flex items-center justify-center rounded hover:bg-surface-muted"
        aria-label="Toggle sidebar"
      >
        <Icon name="Menu" size={20} />
      </button>
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded bg-accent flex items-center justify-center text-white">
          <Icon name="Zap" size={18} />
        </div>
        <div className="leading-tight hidden sm:flex flex-col">
          <span className="text-sm font-semibold text-text">GoFlux</span>
          <span className="text-[10px] text-text-mute uppercase tracking-wide">Super App</span>
        </div>
      </div>
      <div className="ml-4 hidden md:block">
        <PersonaSwitcher />
      </div>
      <div className="flex-1 flex justify-center">
        <button
          type="button"
          onClick={onSearchOpen}
          className="hidden md:flex items-center gap-2 w-80 max-w-full h-9 px-3 rounded border border-border bg-surface-muted text-text-mute text-sm hover:bg-surface"
        >
          <Icon name="Search" size={16} />
          <span>Rechercher...</span>
          <span className="ml-auto inline-flex items-center gap-0.5 text-[10px] border border-border rounded px-1">
            ⌘K
          </span>
        </button>
      </div>
      <button
        type="button"
        onClick={onBellClick}
        className="relative w-9 h-9 flex items-center justify-center rounded hover:bg-surface-muted"
        aria-label="Notifications"
      >
        <Icon name="Bell" size={18} />
        {(inbox?.pendingApprovals ?? 0) > 0 && (
          <span className="absolute top-2 right-2 w-2 h-2 bg-err rounded-full" />
        )}
      </button>
      <button
        type="button"
        onClick={toggleTheme}
        className="w-9 h-9 flex items-center justify-center rounded hover:bg-surface-muted"
        aria-label="Toggle theme"
      >
        <Icon name={isDark ? 'Sun' : 'Moon'} size={18} />
      </button>
      <div ref={menuRef} className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex items-center gap-1 rounded hover:bg-surface-muted pr-2"
          aria-label="User menu"
        >
          <Avatar name={displayName} size={32} />
          <Icon name="ChevronDown" size={14} />
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-full mt-1 w-72 bg-surface border border-border rounded shadow-pop z-50 py-2">
            <div className="px-3 py-2 border-b border-border">
              <div className="text-sm font-semibold text-text truncate">{displayName}</div>
              <div className="text-xs text-text-mute truncate">{user?.email ?? user?.username}</div>
            </div>

            <div className="px-3 py-2">
              <div className="text-[10px] text-text-mute uppercase tracking-wide mb-1.5">Densité</div>
              <div className="flex gap-1">
                {DENSITIES.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => setDensity(d.value)}
                    className={`flex-1 text-xs py-1.5 rounded border ${
                      density === d.value
                        ? 'border-accent bg-accent-soft text-accent-ink'
                        : 'border-border text-text-soft hover:bg-surface-muted'
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="px-3 py-2">
              <div className="text-[10px] text-text-mute uppercase tracking-wide mb-1.5">Accent</div>
              <div className="flex gap-2">
                {ACCENT_HUES.map((h) => (
                  <button
                    key={h.hue}
                    type="button"
                    onClick={() => setAccentHue(h.hue)}
                    title={h.label}
                    className={`w-8 h-8 rounded-full border-2 ${accentHue === h.hue ? 'border-text' : 'border-border'}`}
                    style={{ backgroundColor: h.sample }}
                  />
                ))}
              </div>
            </div>

            <div className="border-t border-border mt-1">
              <button
                type="button"
                onClick={onLogout}
                className="w-full text-left px-3 py-2 text-sm text-err hover:bg-surface-muted flex items-center gap-2"
              >
                <Icon name="LogOut" size={14} />
                Se déconnecter
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
